import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { dateKeyFromPbDate, relativeDayLabel } from "@/lib/dates";
import { useDayKey } from "@/lib/useDayKey";
import { pb } from "@/lib/pb";
import type { TasksResponse } from "@/lib/pb-types";
import { queryKeys } from "@/lib/queryKeys";
import { useAllCards, useProjects } from "@/features/kanban/api";
import { cardTitle } from "@/features/kanban/board";
import { useInboxItems } from "@/features/inbox/hooks";
import { useFlowCards, useFlowCategories } from "@/features/flows/hooks";

import { buildResults, type SearchResult, type SearchSources } from "./search";

/**
 * Every task in the app, title and day only.
 *
 * The Tasks screen never loads more than one day at a time, so search needs its
 * own read. `fields=` trims it to the two columns the index actually shows —
 * a single-user board is a few hundred rows, a handful of kilobytes — and it is
 * cached under the shared `tasks` collection prefix so a realtime write on any
 * day still invalidates it.
 */
function useAllTasks() {
  return useQuery({
    queryKey: queryKeys.collection("tasks", "all"),
    queryFn: () =>
      pb.collection("tasks").getFullList<Pick<TasksResponse, "id" | "title" | "date">>({
        fields: "id,title,date",
        sort: "-date",
        requestKey: null,
      }),
  });
}

/**
 * The dashboard's universal search, wired to live data.
 *
 * Reuses the same queries the surfaces themselves use — `useProjects`,
 * `useAllCards`, `useInboxItems`, the Flows cards — plus one trimmed all-tasks read, so the
 * results can never disagree with what each surface would show. Ranking and
 * routing are `buildResults`' job; this hook only shapes the records into its
 * source lists.
 *
 * `ready` is false until the four reads have first settled, so the field can say
 * "still loading" rather than "no matches" on a query typed the instant the
 * dashboard opens.
 */
export function useSearch(query: string): { results: SearchResult[]; ready: boolean } {
  const today = useDayKey();
  const projects = useProjects();
  const cards = useAllCards();
  const tasks = useAllTasks();
  const inbox = useInboxItems();
  const flowCards = useFlowCards();
  const flowCategories = useFlowCategories();

  const projectById = useMemo(
    () => new Map((projects.data ?? []).map((project) => [project.id, project])),
    [projects.data],
  );

  const sources = useMemo<SearchSources>(
    () => ({
      projects: (projects.data ?? []).map((project) => ({
        id: project.id,
        name: project.name,
        slug: project.slug,
      })),
      // Only cards in live categories: Flows does not list the rest unless their category is opened.
      flows: (flowCards.data ?? []).flatMap((card) => {
        const category = (flowCategories.data ?? []).find((item) => item.id === card.category);
        if (!category || category.archived_at) return [];
        return [{ id: card.id, title: card.title, categoryName: category.name, status: card.status }];
      }),
      inbox: (inbox.data ?? []).map((item) => ({
        id: item.id,
        title: item.title,
        sender: item.sender,
      })),
      tasks: (tasks.data ?? []).map((task) => {
        const key = dateKeyFromPbDate(task.date) ?? today;
        return {
          id: task.id,
          title: task.title,
          dateKey: key,
          subtitle: relativeDayLabel(key, today),
        };
      }),
      cards: (cards.data ?? []).map((card) => {
        const project = projectById.get(card.project);
        return {
          id: card.id,
          title: cardTitle(card),
          issueKey: card.issue_key,
          projectSlug: project?.slug ?? "",
          projectName: project?.name ?? "Unknown project",
        };
      }),
    }),
    [projects.data, flowCards.data, flowCategories.data, inbox.data, tasks.data, cards.data, projectById, today],
  );

  const results = useMemo(() => buildResults(sources, query), [sources, query]);

  const ready =
    !projects.isPending &&
    !cards.isPending &&
    !tasks.isPending &&
    !inbox.isPending &&
    !flowCards.isPending &&
    !flowCategories.isPending;

  return { results, ready };
}
